import { useEffect, useRef, useState } from 'react';

import type { FocusedPhotoStripEntry } from './focusedStripTypes';

export const useFocusedStripPhotoSelection = (visiblePhotos: readonly FocusedPhotoStripEntry[]) => {
  const [selectedPhotoIds, setSelectedPhotoIds] = useState<ReadonlySet<string>>(new Set());
  const selectionAnchorIndexRef = useRef<number | null>(null);

  useEffect(() => {
    const visibleIds = new Set(visiblePhotos.map(photo => photo.photoId));
    setSelectedPhotoIds(previous => {
      const next = new Set([...previous].filter(id => visibleIds.has(id)));
      return next.size === previous.size ? previous : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visiblePhotos.map(photo => photo.photoId).join(',')]);

  const handlePhotoTileClick = (
    photo: FocusedPhotoStripEntry,
    index: number,
    event: React.MouseEvent,
    onSelectPhoto: (photo: FocusedPhotoStripEntry) => void,
  ) => {
    if (event.shiftKey && selectionAnchorIndexRef.current !== null) {
      const [start, end] = [selectionAnchorIndexRef.current, index].sort((left, right) => left - right);
      setSelectedPhotoIds(new Set(visiblePhotos.slice(start, end + 1).map(candidate => candidate.photoId)));
      return;
    }
    if (event.metaKey || event.ctrlKey) {
      selectionAnchorIndexRef.current = index;
      setSelectedPhotoIds(previous => {
        const next = new Set(previous);
        if (next.has(photo.photoId)) next.delete(photo.photoId);
        else next.add(photo.photoId);
        return next;
      });
      return;
    }
    selectionAnchorIndexRef.current = index;
    if (selectedPhotoIds.size) setSelectedPhotoIds(new Set());
    onSelectPhoto(photo);
  };

  return { selectedPhotoIds, handlePhotoTileClick };
};
